import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NgTemplateOutlet } from '@angular/common';
import { ChatService } from '@services/chat-service/chat-service';
import { ConnectionService } from '@services/connection-service';
import { UnpaidPendingCallService } from '@services/pending-call-banner-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { map, take } from 'rxjs/operators';
import { ChatChannel, ChatMessage, ChatMessageInput, ChatMessageOwner } from '@services/chat-service/chat-service.types';
import { EventLoggerService } from '@services/event-logger-service';
import { LanguageService } from '@services/language-service/language.service';
import { VOICE_INPUT_STATE, VoiceInputService, VoiceInputState } from '@services/voice-input-service';
import { appendTranscript, scrollTextFieldToEnd } from '@services/voice-input-service/text-field.utils';
import { VoiceMicInputComponent } from '@components/voice-mic-input/voice-mic-input.component';
import { SessionStorageService } from '@services/session-storage-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { UnpaidUserLlmBotQA } from '@services/experiment/experiment.service.types';
import { AppConfig } from '../../../app.config';
import { PRIMARY_CHANNELS } from '@services/event-logger-service/event-logger.constant';
import { ApiClientConstant } from '@cureskin/api-client';

const CALL_CONFIRMED_QUESTION_ID = 'doctorCallConfirmed';
const SCHEDULE_DOCTOR_CALL_QUESTION_ID = 'requestToScheduleDoctorCall';
const ASKED_QUESTIONS_KEY = 'askedExtraQuestions';
const MIN_THINKING_DISPLAY_MS = 2000;

interface UserQueryChatBottomsheetData {
  user: Table.User;
  initialMessage?: string;
}

interface TreatmentSessionPointer {
  __type: 'Pointer';
  className: 'TreatmentSession';
  objectId: string;
}

interface ExtraQuestion extends UnpaidUserLlmBotQA {
  id: number;
}

interface StoredQAPair {
  afterMessageId: string | null;
  question: string;
  answer: string;
}

/** Parse LanguageString row accessed by a runtime language code (Table.LanguageString.get is strictly keyed). */
interface TranslatedString {
  get: (languageCode: string) => string | undefined;
}

interface ChatBubble {
  id: string;
  isUser: boolean;
  html: string;
  inputs: ChatMessageInput[];
}

export const DEFAULT_QAs: UnpaidUserLlmBotQA[] = [
  { englishQuestion: 'How is my kit designed?',    hindiQuestion: 'मेरी किट कैसे बनाई गई है?',    questionLanguageString: 'wyLqPc0WEl', answerLanguageString: '4bU6nDMM3I' },
  { englishQuestion: 'When will I see results?',   hindiQuestion: 'मुझे नतीजे कब दिखेंगे?',        questionLanguageString: 'lxpPDO5auY', answerLanguageString: 'NpEGUnVsXy' },
  { englishQuestion: 'How long is the treatment?', hindiQuestion: 'इलाज कितने समय का है?',          questionLanguageString: 'AHOQ3cNzwp', answerLanguageString: 'bu5dZvFQB7' },
  { englishQuestion: 'What does the doctor do?',   hindiQuestion: 'डॉक्टर क्या करते हैं?',          questionLanguageString: '9tvPLQcEs0', answerLanguageString: 'CxdNS8y3Bq' },
  { englishQuestion: 'What are monthly checkups?', hindiQuestion: 'मासिक चेकअप क्या होते हैं?',    questionLanguageString: '6yXQMxi5D1', answerLanguageString: 'EXDsyZ3vK5' },
  { englishQuestion: 'Why is photo needed?',       hindiQuestion: 'फोटो की ज़रूरत क्यों है?',       questionLanguageString: '5peyY5oAZP', answerLanguageString: 'OEVivNqgGA' },
];

@Component({
  selector: 'app-user-query-chat-bottomsheet',
  templateUrl: './user-query-chat-bottomsheet.component.html',
  styleUrls: ['./user-query-chat-bottomsheet.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [VoiceMicInputComponent, NgTemplateOutlet],
  providers: [ChatService],
})
export class UserQueryChatBottomsheetComponent implements OnInit, OnDestroy {
  readonly data = inject<UserQueryChatBottomsheetData>(MAT_BOTTOM_SHEET_DATA);
  private readonly appConfig = inject(AppConfig);
  private readonly chatService = inject(ChatService);
  private readonly bottomSheetRef = inject<MatBottomSheetRef<UserQueryChatBottomsheetComponent>>(MatBottomSheetRef);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly connectionService = inject(ConnectionService);
  private readonly pendingCallService = inject(UnpaidPendingCallService);
  private readonly commonUtilityHelper = inject(CommonUtilityHelper);
  private readonly languageService = inject(LanguageService);
  private readonly voiceInputService = inject(VoiceInputService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly experimentService = inject(ExperimentService);

  readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  readonly scrollContent = viewChild<ElementRef<HTMLDivElement>>('scrollContent');
  readonly msgInput = viewChild<ElementRef<HTMLTextAreaElement>>('msgInput');
  readonly msgInputMirror = viewChild<ElementRef<HTMLDivElement>>('msgInputMirror');

  readonly allChats = toSignal(this.chatService.allChats$, { requireSync: true });
  readonly showThinking = toSignal(this.chatService.showThinking$, { requireSync: true });
  readonly loadingOldMessages = toSignal(this.chatService.loadingOldMessages$, { requireSync: true });
  readonly latestMessage = toSignal(this.chatService.latestMessage$, { requireSync: true });
  readonly isVoiceListening = toSignal(
    this.voiceInputService.state$.pipe(map((state: VoiceInputState): boolean => state === VOICE_INPUT_STATE.LISTENING)),
    { requireSync: true },
  );

  readonly initialLoading = signal(false);
  readonly extraQuestions = signal<ExtraQuestion[]>([]);
  /** Mirrors the uncontrolled textarea so the send button and mic tooltip can react to it. */
  readonly draft = signal('');
  readonly voiceMicSupported = signal(false);
  readonly voiceTooltipDismissed = signal(false);

  readonly inputDisabled = computed((): boolean => this.showThinking() || !this.allChats().length);
  readonly isVoiceMicVisible = computed((): boolean => this.voiceMicSupported() && !this.inputDisabled());
  readonly showVoiceTooltip = computed((): boolean => this.isVoiceMicVisible() && !this.voiceTooltipDismissed() && !this.draft());
  readonly hasDraft = computed((): boolean => !!this.draft().trim());
  readonly isDoctorCallRequested = computed((): boolean => this.latestMessage()?.get('questionId') === SCHEDULE_DOCTOR_CALL_QUESTION_ID);
  readonly showInputBar = computed((): boolean => !this.isDoctorCallRequested());
  readonly showExtraQuestions = computed(
    (): boolean => this.extraQuestions().length > 0 && !this.showThinking() && !this.isDoctorCallRequested(),
  );
  readonly chatBubbles = computed((): ChatBubble[] => {
    const chats = this.allChats();
    return chats.map((chat: ChatMessage, index: number): ChatBubble => ({
      id: chat.id,
      isUser: chat.get('Owner') === ChatMessageOwner.USER,
      html: this.commonUtilityHelper.convertTextToHtml(chat.get('Message') ?? ''),
      inputs: this.getActionableInputs(chat, index === chats.length - 1),
    }));
  });

  user: Table.User;
  private treatmentSession: TreatmentSessionPointer;
  private displayLanguage: string;
  private selectedQuestionId: number | null = null;
  private previousChatsLength: number | null = null;
  private draftContainsVoice = false;
  private initialBottomObserver: ResizeObserver | null = null;

  constructor() {
    this.setupBackdropHandler();

    effect((): void => {
      if (this.latestMessage()?.get('questionId') === CALL_CONFIRMED_QUESTION_ID) {
        this.pendingCallService.notifyCallCreated();
      }
    });

    effect((): void => {
      if (this.showThinking()) this.msgInput()?.nativeElement.blur();
    });

    effect((): void => {
      const chatCount = this.allChats().length;
      const isNewMessageAppended = chatCount > 0 && (!this.previousChatsLength || chatCount > this.previousChatsLength);
      if (!untracked(this.loadingOldMessages) && isNewMessageAppended) this.scrollToBottom();
      this.previousChatsLength = chatCount;
    });
  }

  async ngOnInit(): Promise<void> {
    this.initialLoading.set(true);
    this.user = this.data.user;
    this.displayLanguage = this.languageService.getDisplayLanguage(
      this.user?.get('languagePreference') ?? this.appConfig.Shared.Languages.EN,
    );
    this.voiceInputService.reset();
    void this.resolveVoiceMicSupport();
    try {
      await this.initializeChat();
    } catch (error) {
      console.error('Error during initialization:', error);
    } finally {
      this.finishInitialization();
    }
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ name: 'regimen_unpaid_user_ai_chat_bottomsheet_rendered' }), PRIMARY_CHANNELS);
  }

  ngOnDestroy(): void {
    this.initialBottomObserver?.disconnect();
    this.initialBottomObserver = null;
    this.chatService.stopBotReplyTimer();
    this.voiceInputService.reset();
  }

  async sendMessage(text: string): Promise<void> {
    if (this.inputDisabled()) return;
    const message = text.trim();
    // Clear before the empty guard so a whitespace-only draft doesn't linger in the box.
    this.clearDraft();
    if (!message) return;

    if (this.draftContainsVoice) {
      this.voiceInputService.notifyMessageSentWithVoice(message);
      this.draftContainsVoice = false;
    }
    await this.processMessageSend(message);
    this.updateQuestionState();
  }

  onEnterPressed(event: Event): void {
    event.preventDefault();
    void this.sendMessage((event.target as HTMLTextAreaElement).value);
  }

  onSendClicked(): void {
    void this.sendMessage(this.msgInput()?.nativeElement.value ?? this.draft());
  }

  onDraftChange(value: string): void {
    const container = this.scrollContainer()?.nativeElement;
    const wasAtBottom = !!container
      && container.scrollHeight - container.scrollTop - container.clientHeight <= 8;
    this.draft.set(value);
    this.dismissVoiceTooltip();
    this.resizeTextarea(this.msgInput()?.nativeElement);
    if (wasAtBottom) this.scrollToBottom();
  }

  dismissVoiceTooltip(): void {
    this.voiceTooltipDismissed.set(true);
  }

  onVoiceTranscript(transcript: string): void {
    if (!transcript) return;
    const input = this.msgInput()?.nativeElement;
    const next = appendTranscript(input?.value ?? this.draft(), transcript);
    if (input) {
      input.value = next;
      setTimeout((): void => {
        this.resizeTextarea(input);
        scrollTextFieldToEnd(input);
      });
    }
    this.draft.set(next);
    this.draftContainsVoice = true;
    this.dismissVoiceTooltip();
  }

  onVoiceStateChange(state: VoiceInputState): void {
    if (state === VOICE_INPUT_STATE.LISTENING) this.dismissVoiceTooltip();
  }

  async handleInputClick(input: ChatMessageInput): Promise<void> {
    if (!input?.text) return;
    await this.sendMessage(input.text);
  }

  questionLabel(question: ExtraQuestion): string {
    return this.displayLanguage === ApiClientConstant.LanguageString.HI ? question.hindiQuestion : question.englishQuestion;
  }

  async askExtraQuestion(question: ExtraQuestion): Promise<void> {
    this.selectedQuestionId = question.id;

    // Capture the ID of the last real server message before adding any local messages.
    // Used as an anchor so the Q&A pair can be re-inserted at the right position on reopen.
    const afterMessageId = this.getLatestRealMessage()?.id ?? null;

    const questionText = this.questionLabel(question);
    this.chatService.addLocalMessages([this.createLocalChatMessage(ChatMessageOwner.USER, questionText)]);

    this.chatService.setThinking(true);
    const minThinkingDelay = new Promise<void>((resolve): void => {
      setTimeout(resolve, MIN_THINKING_DISPLAY_MS);
    });
    const [fetchResult] = await Promise.allSettled([
      this.fetchAnswerWithFallback(question),
      minThinkingDelay,
    ]);
    this.chatService.setThinking(false);

    const answerText = fetchResult.status === 'fulfilled' ? fetchResult.value : '';
    if (answerText) {
      this.chatService.addLocalMessages([this.createLocalChatMessage(ChatMessageOwner.BOT, answerText)]);
      this.storeLocalQAPair({ afterMessageId, question: questionText, answer: answerText });
    }

    this.updateQuestionState();
  }

  closePopup(): void {
    this.bottomSheetRef.dismiss();
  }

  onScroll(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) return;
    if (container.scrollTop === 0 && !this.loadingOldMessages()) {
      void this.chatService.loadMoreMessages(this.buildChannel());
    }
  }

  private getActionableInputs(chat: ChatMessage, isLatest: boolean): ChatMessageInput[] {
    if (!isLatest) return [];
    const inputs: ChatMessageInput[] = chat.get('Inputs') ?? [];
    if (!inputs.length || inputs[0]?.type === ApiClientConstant.Question.InputType.TEXT) return [];
    return inputs;
  }

  private async initializeChat(): Promise<void> {
    await this.loadExtraQuestions();
    this.filterAskedQuestions();
    await this.loadUserAndTreatmentSession();
    await this.loadChatHistory();
    await this.handleInitialMessage();
  }

  private setupBackdropHandler(): void {
    this.bottomSheetRef.backdropClick().pipe(take(1)).subscribe((): void => {
      const input = this.msgInput()?.nativeElement;
      if (input) {
        input.disabled = true;
        input.blur();
      }
    });
  }

  private finishInitialization(): void {
    this.initialLoading.set(false);
    this.scrollToBottom();
    this.trackInitialContentGrowth();
  }

  private trackInitialContentGrowth(): void {
    if (typeof ResizeObserver === 'undefined') return;

    setTimeout((): void => {
      const content = this.scrollContent()?.nativeElement;
      if (!content) return;

      let previousHeight = content.getBoundingClientRect().height;
      const observer = new ResizeObserver((): void => {
        const currentHeight = content.getBoundingClientRect().height;
        if (currentHeight === previousHeight) return;
        previousHeight = currentHeight;
        this.scrollToBottom();
      });

      this.initialBottomObserver?.disconnect();
      this.initialBottomObserver = observer;
      observer.observe(content);

      setTimeout((): void => {
        if (this.initialBottomObserver !== observer) return;
        observer.disconnect();
        this.initialBottomObserver = null;
      }, 1500);
    });
  }

  private clearDraft(): void {
    this.draft.set('');
    const input = this.msgInput()?.nativeElement;
    if (input) {
      input.blur();
      input.value = '';
      this.resizeTextarea(input);
    }
  }

  private resizeTextarea(input: HTMLTextAreaElement | undefined): void {
    const mirror = this.msgInputMirror()?.nativeElement;
    if (!input || !mirror) return;

    mirror.style.width = `${input.clientWidth}px`;
    mirror.textContent = `${input.value || ' '}\u200b`;

    const minHeight = 48;
    const maxHeight = 120;
    const contentHeight = mirror.scrollHeight;
    input.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`;
    input.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }

  private async processMessageSend(text: string): Promise<void> {
    const message = text.trim();
    // Local Q&A messages have no className — ChatService.createReplyMessage needs a real
    // Parse object's className to construct the reply. Skip local messages when looking
    // for the last message reference.
    const lastMessage = [...this.allChats()].reverse().find((chat: ChatMessage): boolean => !!chat.className) ?? null;

    await this.chatService.sendMessage(this.buildChannel(), lastMessage, message);
  }

  private buildChannel(): ChatChannel {
    return {
      user: this.user,
      parent: this.treatmentSession,
      parentField: 'treatmentSession',
      fetchChats: (payload): Promise<ChatMessage[]> => this.connectionService.fetchTreatmentChat(payload),
    };
  }

  private updateQuestionState(): void {
    if (this.selectedQuestionId === null) return;

    this.extraQuestions.update(
      (questions: ExtraQuestion[]): ExtraQuestion[] => questions.filter(
        (question: ExtraQuestion): boolean => question.id !== this.selectedQuestionId,
      ),
    );

    const askedIds = this.sessionStorage.getJson<number[]>(ASKED_QUESTIONS_KEY, []);
    this.sessionStorage.setJson(ASKED_QUESTIONS_KEY, [...askedIds, this.selectedQuestionId]);
    this.selectedQuestionId = null;
  }

  private scrollToBottom(): void {
    setTimeout((): void => {
      const container = this.scrollContainer()?.nativeElement;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
      requestAnimationFrame((): void => {
        const settledContainer = this.scrollContainer()?.nativeElement;
        if (settledContainer) settledContainer.scrollTop = settledContainer.scrollHeight;
      });
    });
  }

  private async loadExtraQuestions(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    const qas = this.experimentService.getUnpaidUserLlmBotExperiment()?.variant?.defaultQAs ?? DEFAULT_QAs;
    this.extraQuestions.set(qas.map((qa: UnpaidUserLlmBotQA, index: number): ExtraQuestion => ({ id: index, ...qa })));
  }

  private filterAskedQuestions(): void {
    const askedIds = this.sessionStorage.getJson<number[]>(ASKED_QUESTIONS_KEY, []);
    this.extraQuestions.update(
      (questions: ExtraQuestion[]): ExtraQuestion[] => questions.filter(
        (question: ExtraQuestion): boolean => !askedIds.includes(question.id),
      ),
    );
  }

  private async loadUserAndTreatmentSession(): Promise<void> {
    this.user = await this.connectionService.getActingUser();
    const treatmentSessionResponse = await this.connectionService.findTreatmentSession();
    this.treatmentSession = {
      __type: 'Pointer',
      className: 'TreatmentSession',
      objectId: treatmentSessionResponse?.treatmentSession?.objectId,
    };
  }

  private async loadChatHistory(): Promise<void> {
    await this.chatService.loadChatHistory(this.buildChannel());
    this.restoreLocalQAs();
  }

  private async handleInitialMessage(): Promise<void> {
    if (!this.data?.initialMessage?.trim()) {
      this.scrollToBottom();
      return;
    }

    const lastMessage = this.getLatestRealMessage();
    if (lastMessage) {
      await this.chatService.sendMessage(this.buildChannel(), lastMessage, this.data.initialMessage.trim());
      delete this.data.initialMessage;
    }
    this.scrollToBottom();
  }

  private async resolveVoiceMicSupport(): Promise<void> {
    const supported = await this.voiceInputService.isMicAvailable();
    this.voiceMicSupported.set(supported);
  }

  private async fetchAnswerWithFallback(question: ExtraQuestion): Promise<string> {
    const extractAnswer = (strings: TranslatedString[]): string => strings?.[0]?.get(this.displayLanguage)
      || strings?.[0]?.get(ApiClientConstant.LanguageString.EN)
      || '';

    try {
      const strings = await this.connectionService.getLanguageStringsById([question.answerLanguageString]);
      const text = extractAnswer(strings);
      if (text) return text;
    } catch { /* fall through to DEFAULT_QAs retry */ }

    const defaultQA = DEFAULT_QAs.find((qa: UnpaidUserLlmBotQA): boolean => qa.englishQuestion === question.englishQuestion);
    if (!defaultQA || defaultQA.answerLanguageString === question.answerLanguageString) return '';

    try {
      const fallbackStrings = await this.connectionService.getLanguageStringsById([defaultQA.answerLanguageString]);
      return extractAnswer(fallbackStrings);
    } catch {
      return '';
    }
  }

  private restoreLocalQAs(): void {
    const pairs = this.sessionStorage.getJson<StoredQAPair[]>(this.getLocalQAsStorageKey(), []);
    if (!pairs.length) return;

    const merged = [...this.allChats()];
    for (const pair of pairs) {
      const anchorIndex = pair.afterMessageId
        ? merged.findIndex((chat: ChatMessage): boolean => chat.id === pair.afterMessageId)
        : -1;
      const latestRealMessageIndex = this.findLatestRealMessageIndex(merged);
      let insertAt = latestRealMessageIndex;
      if (anchorIndex !== -1) {
        insertAt = anchorIndex + 1;
      } else if (latestRealMessageIndex === -1) {
        insertAt = merged.length;
      }
      merged.splice(
        insertAt, 0,
        this.createLocalChatMessage(ChatMessageOwner.USER, pair.question),
        this.createLocalChatMessage(ChatMessageOwner.BOT, pair.answer),
      );
    }

    this.chatService.replaceMessages(merged);
  }

  private getLatestRealMessage(): ChatMessage | null {
    return [...this.allChats()].reverse().find((chat: ChatMessage): boolean => !!chat.className) ?? null;
  }

  private findLatestRealMessageIndex(messages: ChatMessage[]): number {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].className) return index;
    }
    return -1;
  }

  private storeLocalQAPair(pair: StoredQAPair): void {
    const key = this.getLocalQAsStorageKey();
    const pairs = this.sessionStorage.getJson<StoredQAPair[]>(key, []);
    this.sessionStorage.setJson(key, [...pairs, pair]);
  }

  private getLocalQAsStorageKey(): string {
    return `localQAPairs_${this.user?.id}_${this.treatmentSession?.objectId}`;
  }

  private createLocalChatMessage(owner: ChatMessageOwner, text: string): ChatMessage {
    const data: Record<string, string> = { Owner: owner, Message: text };
    return {
      id: `local_${Date.now()}_${Math.random()}`,
      message: text,
      owner,
      createdAt: new Date().toISOString(),
      get: (key: string): string => data[key],
    };
  }
}
